import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Modal, Form, Row, Col } from 'react-bootstrap'
import Message from '../../shared_components/Message'
import Loader from '../../shared_components/Loader'

import {
  createJitaiResp,
  updateJitaiResp,
} from '../actions/jitaiActions'
import {
  JITAI_RESP_CREATE_RESET,
  JITAI_RESP_UPDATE_RESET,
} from '../constants/jitaiConstants'

const UpsertJitaiResp = ({
  studyId,
  jitaiRespList,
  updatedJitaiResp,
  qxList,
}) => {
  const [show, setShow] = useState(false)
  const [code, setCode] = useState('')
  const [respType, setRespType] = useState('')
  const [questionnaire, setQuestionnaire] = useState('')
  const [customImplementationNotes, setCustomImplementationNotes] = useState('')

  const dispatch = useDispatch()

  const jitaiRespCreate = useSelector((state) => state.jitaiRespCreate)
  const {
    loading: loadingCreate,
    error: errorCreate,
    success: successCreate,
  } = jitaiRespCreate

  const jitaiRespUpdate = useSelector((state) => state.jitaiRespUpdate)
  const {
    loading: loadingUpdate,
    error: errorUpdate,
    success: successUpdate,
  } = jitaiRespUpdate

  const isEditMode = !!updatedJitaiResp

  useEffect(() => {
    if (isEditMode && updatedJitaiResp) {
      setCode(updatedJitaiResp.code)
      setRespType(updatedJitaiResp.resp_type)
      setQuestionnaire(updatedJitaiResp.questionnaire?._id || updatedJitaiResp.questionnaire || '')
      setCustomImplementationNotes(updatedJitaiResp.custom_implementation_notes || '')
    }
  }, [isEditMode, updatedJitaiResp])

  useEffect(() => {
    if (successCreate || successUpdate) {
      setShow(false)
      resetForm()
      dispatch({ type: JITAI_RESP_CREATE_RESET })
      dispatch({ type: JITAI_RESP_UPDATE_RESET })
    }
  }, [successCreate, successUpdate, dispatch])

  const resetForm = () => {
    setCode('')
    setRespType('')
    setQuestionnaire('')
    setCustomImplementationNotes('')
  }

  const handleClose = () => {
    setShow(false)
    resetForm()
  }

  const handleShow = () => {
    if (isEditMode && updatedJitaiResp) {
      setCode(updatedJitaiResp.code)
      setRespType(updatedJitaiResp.resp_type)
      setQuestionnaire(updatedJitaiResp.questionnaire?._id || updatedJitaiResp.questionnaire || '')
      setCustomImplementationNotes(updatedJitaiResp.custom_implementation_notes || '')
    }
    setShow(true)
  }

  const submitHandler = (e) => {
    e.preventDefault()
    const jitaiRespData = {
      code,
      resp_type: respType,
    }
    
    if (respType === 'qx') {
      jitaiRespData.questionnaire = questionnaire
    } else if (respType === 'custom') {
      jitaiRespData.custom_implementation_notes = customImplementationNotes
    }

    if (isEditMode) {
      dispatch(
        updateJitaiResp(studyId, updatedJitaiResp._id, jitaiRespData)
      )
    } else {
      dispatch(createJitaiResp(studyId, jitaiRespData))
    }
  }

  return (
    <>
      <Button variant='primary' className='btn-sm' onClick={handleShow}>
        {isEditMode ? 'Edit' : 'Add'}
      </Button>

      <Modal show={show} onHide={handleClose} size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? 'Edit' : 'Add'} Jitai Response
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingCreate || loadingUpdate ? (
            <Loader />
          ) : (
            <Form onSubmit={submitHandler}>
              <Row>
                <Col md={6}>
                  <Form.Group controlId='code'>
                    <Form.Label>Code</Form.Label>
                    <Form.Control
                      type='text'
                      placeholder='Enter unique code'
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                    />
                    <Form.Text className='text-muted'>
                      A unique identifier for this response definition
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId='respType'>
                    <Form.Label>Response Type</Form.Label>
                    <Form.Control
                      as='select'
                      value={respType}
                      onChange={(e) => {
                        setRespType(e.target.value)
                        // Reset type-specific fields
                        setQuestionnaire('')
                        setCustomImplementationNotes('')
                      }}
                      required
                    >
                      <option value=''>—Select Type—</option>
                      <option value='qx'>Questionnaire (qx)</option>
                      <option value='custom'>Custom</option>
                    </Form.Control>
                    <Form.Text className='text-muted'>
                      qx: collect via questionnaire; custom: developer-defined collection
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              {respType === 'qx' && (
                <Row className='mt-3'>
                  <Col md={12}>
                    <Form.Group controlId='questionnaire'>
                      <Form.Label>Questionnaire</Form.Label>
                      <Form.Control
                        as='select'
                        value={questionnaire}
                        onChange={(e) => setQuestionnaire(e.target.value)}
                        required
                      >
                        <option value=''>—Select Questionnaire—</option>
                        {qxList && qxList.map((qx) => (
                          <option key={qx._id} value={qx._id}>
                            {qx.chin_name} ({qx.abbrev})
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Text className='text-muted'>
                        The questionnaire to be presented for response collection
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {respType === 'custom' && (
                <Row className='mt-3'>
                  <Col md={12}>
                    <Form.Group controlId='customImplementationNotes'>
                      <Form.Label>Custom Implementation Notes</Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={6}
                        placeholder='Describe how this custom response should be collected. This is for developer reference when implementing the data collection logic.'
                        value={customImplementationNotes}
                        onChange={(e) => setCustomImplementationNotes(e.target.value)}
                      />
                      <Form.Text className='text-muted'>
                        Developer notes on how to implement data collection for this custom response type
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              {errorCreate && <Message variant='danger'>{errorCreate}</Message>}
              {errorUpdate && <Message variant='danger'>{errorUpdate}</Message>}

              <div className='mt-3'>
                <Button
                  type='submit'
                  variant='primary'
                  style={{ marginRight: '12px' }}
                >
                  {isEditMode ? 'Update' : 'Create'}
                </Button>
                <Button variant='secondary' onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default UpsertJitaiResp

